// AI Conversation Component — OFFSET
// Container for chat messages with auto-scroll and scroll button

import React, { useRef, useEffect, useState, memo } from 'react';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

export interface AIConversationProps {
    className?: string;
    children: React.ReactNode;
}

export const AIConversation = memo(({
    className,
    children
}: AIConversationProps) => {
    return (
        <div className={cn("flex flex-col h-full overflow-hidden relative", className)}>
            {children}
        </div>
    );
});

AIConversation.displayName = 'AIConversation';

// Scrollable content area
export interface AIConversationContentProps {
    className?: string;
    children: React.ReactNode;
    onScrollChange?: (isAtBottom: boolean) => void;
}

export const AIConversationContent = memo(({
    className,
    children,
    onScrollChange
}: AIConversationContentProps) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    // Check if scrolled to bottom
    const checkScrollPosition = () => {
        if (!scrollRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 50;
        setIsAtBottom(atBottom);
        onScrollChange?.(atBottom);
    };

    // Auto-scroll to bottom when children change
    useEffect(() => {
        if (scrollRef.current && isAtBottom) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [children, isAtBottom]);

    return (
        <div
            ref={scrollRef}
            onScroll={checkScrollPosition}
            className={cn(
                "flex-1 overflow-y-auto px-4 py-4",
                className
            )}
        >
            <div className="space-y-1">
                {children}
            </div>

            {/* Bottom padding for scroll */}
            <div className="h-4" />
        </div>
    );
});

AIConversationContent.displayName = 'AIConversationContent';

// Scroll to bottom button
export interface AIConversationScrollButtonProps {
    className?: string;
    onClick?: () => void;
    visible?: boolean;
}

export const AIConversationScrollButton = memo(({
    className,
    onClick,
    visible = true
}: AIConversationScrollButtonProps) => {
    if (!visible) return null;

    return (
        <button
            onClick={onClick}
            className={cn(
                "absolute bottom-4 left-1/2 -translate-x-1/2",
                "flex items-center gap-1.5 px-3 py-1.5",
                "bg-card hover:bg-secondary",
                "border border-hairline-strong",
                "text-xs text-muted-foreground hover:text-foreground",
                "transition-colors",
                className
            )}
        >
            <ChevronDown className="w-3 h-3" />
            <span>Nouveaux messages</span>
        </button>
    );
});

AIConversationScrollButton.displayName = 'AIConversationScrollButton';

// Hook for managing conversation scroll
export function useConversationScroll() {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth'
            });
        }
    };

    const checkScrollPosition = () => {
        if (!scrollRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const atBottom = scrollHeight - scrollTop - clientHeight < 50;
        setIsAtBottom(atBottom);
    };

    return {
        scrollRef,
        isAtBottom,
        scrollToBottom,
        checkScrollPosition
    };
}

export default AIConversation;
